import { createContext, useContext, useMemo } from 'react'

import {
  APP_MALFUNCTION_SETS,
  DEFAULT_COUNTRY,
} from '@ngb-frontend/shared/config'
import { getBusinessRules } from '@ngb-frontend/shared/rules'

import type {
  CountryConfig,
  QueryCountryCode,
} from '@ngb-frontend/shared/types'
import type { PropsWithChildren } from 'react'

// LeasePlan's static country config, sourced from the country registry's LP
// `remote` defaults (Ayvens gets the equivalent from Salesforce fe-settings).
// Malfunctions are the full static catalogue.
const lpRemote = getBusinessRules('LPUK').remote
const LP_COUNTRY_CONFIG: Omit<
  CountryConfig,
  'countryCode' | 'is' | 'isAyvens'
> = {
  detailedRPV: lpRemote.detailedRPV,
  additionalServices: lpRemote.additionalServices,
  malfunctions: APP_MALFUNCTION_SETS,
  minBookingDaysDriver: lpRemote.minBookingDaysDriver,
  minBookingDaysAgent: lpRemote.minBookingDaysAgent,
  autopickPNRSupplier: false,
}

const CountryConfigContext = createContext<CountryConfig | null | undefined>(
  undefined,
)

const getIsForCountry = (configCountry: QueryCountryCode) => ({
  Sweden: configCountry === '75201' || configCountry === '75202',
  Austria: configCountry === '4001',
  Switzerland: configCountry === '75601',
  Romania: configCountry === '64201',
  Finland: configCountry === '24601',
  Bulgaria: configCountry === '10001',
  Czechia: configCountry === '20301',
  Serbia: configCountry === '68801',
  Luxembourg: configCountry === '44201',
  // The "Default" business-rules superset is owned by the country registry
  Default: getBusinessRules(configCountry).services.isDefaultRuleset,
  Global: configCountry === 'GLOBAL', // fallback for not mapped countries
  UK: configCountry === 'LPUK',
})

export const CountryConfigProvider: React.FC<
  PropsWithChildren<{
    country?: QueryCountryCode
    isAyvens?: boolean
    countryConfig?: Omit<CountryConfig, 'countryCode' | 'isAyvens' | 'is'>
  }>
> = ({ children, countryConfig, country, isAyvens = false }) => {
  const configCountry = country || DEFAULT_COUNTRY

  const appCountryConfig: CountryConfig | null | undefined = useMemo(() => {
    const baseConfig = { countryCode: configCountry }
    const is = getIsForCountry(configCountry)

    if (!isAyvens) return { ...baseConfig, ...LP_COUNTRY_CONFIG, isAyvens, is }

    return countryConfig
      ? { countryCode: configCountry, ...countryConfig, isAyvens, is }
      : // TODO: Handle fallback (null for now means failure to fetch for Ayvens countries)
        null
  }, [configCountry, countryConfig, isAyvens])

  return (
    <CountryConfigContext.Provider value={appCountryConfig}>
      {children}
    </CountryConfigContext.Provider>
  )
}

export const useCountryConfig = () => {
  const countryConfig = useContext(CountryConfigContext)
  if (countryConfig === undefined) {
    throw new Error(
      'No Context value found for `CountryConfig`. Did you forget to wrap your application in the `CountryConfigProvider`?',
    )
  }

  if (!countryConfig) return

  return countryConfig
}
